import type { Dictionary } from "./types";

export const en: Dictionary = {
  common: {
    skipToContent: "Skip to content",
    brandName: "STRIDE",
    titleTemplate: (pageTitle) => `${pageTitle} — STRIDE`,
    itemCount: (quantity) => `${quantity} ${quantity === 1 ? "item" : "items"}`,
  },
  languageSwitch: {
    label: "Language selector",
  },
  header: {
    promoShipping: "Free shipping over $150 USD",
    promoReturns: "30-day returns",
    promoCta: "Shop new arrivals ↗",
    brandAria: "STRIDE, go to homepage",
    navAria: "Main navigation",
    navHome: "Home",
    navShop: "Shop",
    menuOpenLabel: "Menu",
    menuCloseLabel: "Close",
    menuOpenAria: "Open navigation",
    menuCloseAria: "Close navigation",
    cartLabel: "Cart",
    cartAria: (itemsLabel) => `Cart, ${itemsLabel}`,
  },
  footer: {
    lead: "Move with intention.",
    viewCollection: "View collection ↗",
    tagline: "Educational e-commerce project.",
    copyright: "© 2026 STRIDE / Bogotá",
  },
  home: {
    pageTitle: "Home",
    kicker: "Collection 01 / 2026",
    titleLine1: "Push",
    titleLine2: "your pace",
    intro:
      "Gear designed to move with you. From the first step to the last mile.",
    ctaShop: "Shop now",
    ctaNews: "See what's new",
    heroImageAlt: "Athlete running outdoors during a training session",
    heroStampLabel: "RUN / TRAIN / LIVE",
    metaBrand: "STRIDE / MOVE FORWARD",
    metaLocation: "BOG / 4.7110° N",
    categoriesAria: "Main categories",
    catRun: "Run",
    catTrain: "Train",
    catLive: "Live",
    featuredKicker: "Selection / 01",
    featuredTitle: "New drops move fast.",
    viewAll: "View all",
    editorialImageAlt: "Athlete strength training in a bright gym",
    editorialStamp: "TRAIN / 02",
    editorialKicker: "Designed for movement",
    editorialTitle: "No excuses. No shortcuts.",
    editorialBody:
      "Essential pieces, durable materials, and everyday comfort. Your pace changes; your gear keeps up.",
    editorialCta: "Discover training",
    manifestoKicker: "STRIDE / Manifesto",
    manifestoText:
      "It doesn't matter where you start. What matters is that you keep moving forward.",
    manifestoFooter: "Made for every step.",
  },
  shop: {
    pageTitle: "Shop",
    kicker: "Shop / Full collection",
    season: "2026 Season",
    titleLine1: "Gear up.",
    titleLine2: "Beat your best.",
    intro:
      "Footwear and accessories selected for training, the street, and everything in between.",
    scrollHint: "Scroll to explore ↓",
    loadingKicker: "Shop",
    loadingTitle: "Preparing the collection…",
  },
  catalog: {
    filtersAria: "Filter products",
    filterAll: "All",
    filterShoes: "Footwear",
    filterAccessories: "Accessories",
    sortLabel: "Sort",
    sortFeatured: "Featured",
    sortPriceLow: "Price: low to high",
    sortPriceHigh: "Price: high to low",
    heading: "Full collection",
    count: (visibleCount) => `${String(visibleCount).padStart(2, "0")} products`,
  },
  audiences: {
    men: "Men",
    women: "Women",
    unisex: "Unisex",
  },
  product: {
    newBadge: "New",
    ratingAria: (rating) => `${rating} out of 5 stars`,
    add: "Add to cart",
    added: "Added ✓",
    alt: (title, category) =>
      `${title}, ${category === "shoes" ? "footwear" : "accessories"} product from the STRIDE collection`,
  },
  quantity: {
    label: (name) => `Quantity for ${name}`,
    decreaseAria: (name) => `Decrease quantity for ${name}`,
    removeAtZeroAria: (name) => `Remove ${name} from cart`,
    increaseAria: (name) => `Increase quantity for ${name}`,
  },
  cart: {
    pageTitle: "Cart",
    loading: "Loading your gear…",
    kicker: (lineCount) => `Cart / ${String(lineCount).padStart(2, "0")}`,
    emptyTitle: "Your cart hasn't started moving yet.",
    emptyBody:
      "Find the gear that matches your pace and come back when you're ready.",
    exploreCta: "Explore the shop",
    title: "Your gear.",
    itemsHeading: "Items",
    clear: "Clear cart",
    perUnit: "/ unit",
    remove: "Remove",
    summaryKicker: "Summary",
    summaryTitle: "Order total",
    subtotal: "Subtotal",
    shipping: "Shipping",
    free: "Free",
    total: "Total",
    progressAria: "Progress toward free shipping",
    remaining: (formattedAmount) =>
      `${formattedAmount} USD left for free shipping.`,
    earned: "✓ Your order qualifies for free shipping.",
    checkout: "Checkout",
    demoCaption:
      "Educational demo: no data is collected and no payment is processed.",
    demoNotice: "Nice pace! Checkout ends here in this demo.",
  },
  cartStatus: {
    added: (quantity, title) =>
      `${quantity} ${quantity === 1 ? "unit" : "units"} of ${title} added`,
    updated: "Quantity updated",
    removed: "Product removed from cart",
    cleared: "Cart cleared",
  },
  notFound: {
    kicker: "Error / 404",
    title: "This route doesn't lead anywhere.",
    body: "Get back on track and keep exploring the collection.",
    cta: "Back to home",
  },
};
